import { useAppSelector } from "app/hooks";
import { ReactNode } from "react";
import { GuxInlineAlert } from "genesys-spark-components-react";

import { useStrings } from "features/i18n/useStrings";
import strings from "./AlertMessage.strings";
import "./AlertMessage.less";

import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";

import {
    selectCanUserViewAiScoringExplanation,
    selectCanUserViewSTATopics,
} from "features/auth/currentUserSlice";
import {
    selectIsEvaluationAssistanceConfigured,
    selectIsAiScoringConfiguredForEvaluation,
} from "features/qualityForm/qualityFormSlice";
import {
    selectDidAiScoringProcessingFail,
    selectIsAiScoringDuplicateAutomatedFormWithCopiedScore,
    selectIsAiScoringDuplicateFormSameAgent,
    selectIsAiScoringQuotaReached,
    selectIsAiScoringUnauthorizedForOrg,
} from "features/scorable/scorableSlice";

interface AlertProps {
    message: string;
    accent?: "error" | "warning" | "info";
    className?: string;
    "data-testid"?: string;
}

function Alert({
    message,
    accent = "error",
    className = "alert",
    "data-testid": dataTestId = "alert",
}: AlertProps) {
    return (
        <GuxInlineAlert
            accent={accent}
            className={className}
            data-testid={dataTestId}
        >
            {message}
        </GuxInlineAlert>
    );
}

export function AlertMessage({
    isAdmin,
    children,
}: {
    isAdmin: boolean;
    children?: ReactNode;
}) {
    const t = useStrings(strings);
    const isAiScoringFTEnabled = useAppSelector((state) => {
        return selectFeatureToggle(state, FeatureToggle.AI_SCORING);
    });

    // Evaluation Assistance
    const hasEvaluationAssistanceFailed = useAppSelector(
        (state) =>
            (state.scorable.data?.scorable as any)?.hasAssistanceFailed ?? false
    );

    const isEvaluationAssistanceConfigured = useAppSelector(
        selectIsEvaluationAssistanceConfigured
    );

    const hasSTAPermission = useAppSelector(selectCanUserViewSTATopics);

    // AI Scoring
    const isAiScoringConfigured = useAppSelector((state) => {
        if (isAiScoringFTEnabled) {
            return selectIsAiScoringConfiguredForEvaluation(state);
        } else {
            return false;
        }
    });

    const hasAiScoringExplanationPermission = useAppSelector(
        selectCanUserViewAiScoringExplanation
    );

    // Alert visibility conditions
    const showAiScoringProcessingFailure =
        useAppSelector(selectDidAiScoringProcessingFail) && isAdmin;
    const showAiScoringQuotaReached =
        useAppSelector(selectIsAiScoringQuotaReached) && isAdmin;
    const showAiScoringDuplicateFormSameAgent =
        useAppSelector(selectIsAiScoringDuplicateFormSameAgent) && isAdmin;
    const showAiScoringUnauthorizedFailure =
        useAppSelector(selectIsAiScoringUnauthorizedForOrg) && isAdmin;
    const showAiScoringDuplicateAutomatedFormWithCopiedScore =
        useAppSelector(
            selectIsAiScoringDuplicateAutomatedFormWithCopiedScore
        ) && isAdmin;

    const showEvaluationAssistanceFailure =
        isAdmin && hasEvaluationAssistanceFailed;

    const hasAiScoringPermissionError =
        isAdmin && isAiScoringConfigured && !hasAiScoringExplanationPermission;
    const hasEvaluationAssistancePermissionError =
        isAdmin && isEvaluationAssistanceConfigured && !hasSTAPermission;

    let showPermissionError = false;
    let permissionBannerMessage = "";

    if (isAiScoringFTEnabled) {
        showPermissionError =
            hasAiScoringPermissionError ||
            hasEvaluationAssistancePermissionError;
        if (
            hasAiScoringPermissionError &&
            hasEvaluationAssistancePermissionError
        ) {
            permissionBannerMessage =
                t.labels.insufficientPermissions.topicAndTranscriptView;
        } else if (hasAiScoringPermissionError) {
            permissionBannerMessage =
                t.labels.insufficientPermissions.transcriptView;
        } else if (hasEvaluationAssistancePermissionError) {
            permissionBannerMessage =
                t.labels.insufficientPermissions.topicView;
        }
    } else {
        showPermissionError =
            isAdmin && !hasSTAPermission && isEvaluationAssistanceConfigured;
        permissionBannerMessage = showPermissionError
            ? t.labels.insufficientPermissions.topicView
            : "";
    }

    return (
        <div className="AlertMessage" data-testid="alert-message">
            {showAiScoringProcessingFailure && (
                <Alert message={t.labels.aiScoringProcessingFailure} />
            )}
            {showAiScoringUnauthorizedFailure && (
                <Alert message={t.labels.aiScoringUnauthorizedFailure} />
            )}
            {showAiScoringQuotaReached && (
                <Alert message={t.labels.aiScoringQuotaReached} />
            )}
            {showAiScoringDuplicateFormSameAgent && (
                <Alert message={t.labels.aiScoringDuplicateFormSameAgent} />
            )}
            {showEvaluationAssistanceFailure && (
                <Alert message={t.labels.evaluationAssistanceFailed} />
            )}
            {showAiScoringDuplicateAutomatedFormWithCopiedScore && (
                <Alert
                    message={
                        t.labels.aiScoringDuplicateAutomatedFormWithCopiedScore
                    }
                    accent="info"
                    className="duplicate-automated-form-with-copied-score-info"
                    data-testid="duplicate-automated-form-with-copied-score-info"
                />
            )}
            {showPermissionError && (
                <Alert
                    message={permissionBannerMessage}
                    accent="warning"
                    className="permission-error"
                    data-testid="permission-error"
                />
            )}
            {children}
        </div>
    );
}
